import { JSErrors } from '@newrelic/browser-agent/features/jserrors';
import { SessionTrace } from '@newrelic/browser-agent/features/session_trace';
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

let agent: BrowserAgent | null = null;
let initialized = false;
let lastUserId: string | null = null;

const isDisabled = (): boolean =>
  import.meta.env.DEV && import.meta.env.VITE_NEW_RELIC_DEBUG !== 'true';

const initialize = (): void => {
  if (initialized) {
    return;
  }

  if (isDisabled()) {
    initialized = true;
    return;
  }

  const licenseKey = import.meta.env.VITE_NEW_RELIC_LICENSE_KEY;
  const applicationID = import.meta.env.VITE_NEW_RELIC_APP_ID;
  const accountID = import.meta.env.VITE_NEW_RELIC_ACCOUNT_ID;

  if (!licenseKey || !applicationID || !accountID) {
    console.warn('New Relic: Missing required configuration. Skipping initialization.');
    initialized = true;
    return;
  }

  agent = new BrowserAgent({
    init: {
      distributed_tracing: { enabled: true },
      performance: { capture_measures: true },
      browser_consent_mode: { enabled: false },
      privacy: { cookies_enabled: true },
      ajax: { deny_list: ['bam.eu01.nr-data.net'] },
      logging: { enabled: true },
    },
    info: {
      beacon: 'bam.eu01.nr-data.net',
      errorBeacon: 'bam.eu01.nr-data.net',
      licenseKey,
      applicationID,
      sa: 1,
    },
    loader_config: {
      accountID,
      trustKey: import.meta.env.VITE_NEW_RELIC_TRUST_KEY || accountID,
      agentID: import.meta.env.VITE_NEW_RELIC_AGENT_ID || applicationID,
      licenseKey,
      applicationID,
    },
    features: [SessionTrace, JSErrors],
  });

  // Set application version from build
  if (typeof APP_VERSION !== 'undefined') {
    agent.setApplicationVersion(APP_VERSION);
  }

  // Wrap console methods to automatically capture logs
  agent.wrapLogger(console, 'log', { level: 'INFO' });
  agent.wrapLogger(console, 'info', { level: 'INFO' });
  agent.wrapLogger(console, 'warn', { level: 'WARN' });
  agent.wrapLogger(console, 'error', { level: 'ERROR' });
  agent.wrapLogger(console, 'debug', { level: 'DEBUG' });

  initialized = true;
};

const setUserId = (userId: string | null): void => {
  if (isDisabled()) {
    return;
  }

  if (!initialized || !agent) {
    console.warn('New Relic: Not initialized. User ID not set:', userId);
    return;
  }

  if (lastUserId === userId) {
    return;
  }

  lastUserId = userId;
  agent.setUserId(userId);
  // Also expose as a custom attribute so it rides on every NR log line —
  // setUserId's enduser.id does not reliably reach the Logging feature.
  agent.setCustomAttribute('userId', userId, true);
};

const getUserId = (): string | null => {
  return lastUserId;
};

const setApplicationVersion = (version: string | null): void => {
  if (isDisabled()) {
    return;
  }

  if (!initialized || !agent) {
    console.warn('New Relic: Not initialized. Application version not set:', version);
    return;
  }

  agent.setApplicationVersion(version);
};

const addPageAction = (name: string, attributes?: Record<string, unknown>): void => {
  if (isDisabled()) {
    return;
  }

  if (!initialized || !agent) {
    console.warn('New Relic: Not initialized. Page action not logged:', name);
    return;
  }

  agent.addPageAction(name, attributes);
};

const recordCustomEvent = (eventType: string, attributes?: Record<string, unknown>): void => {
  if (isDisabled()) {
    return;
  }

  if (!initialized || !agent) {
    console.warn('New Relic: Not initialized. Custom event not recorded:', eventType);
    return;
  }

  agent.recordCustomEvent(eventType, attributes);
};

const noticeError = (error: Error | string, customAttributes?: Record<string, unknown>): void => {
  if (isDisabled()) {
    return;
  }

  if (!initialized || !agent) {
    const message = error instanceof Error ? error.message : error;
    console.warn('New Relic: Not initialized. Error not reported:', message);
    return;
  }

  agent.noticeError(error, customAttributes);
};

const setCustomAttribute = (
  name: string,
  value: string | number | boolean | null,
  persist?: boolean,
): void => {
  if (isDisabled()) {
    return;
  }

  if (!initialized || !agent) {
    console.warn('New Relic: Not initialized. Custom attribute not set:', name);
    return;
  }

  agent.setCustomAttribute(name, value, persist);
};

const setPageViewName = (name: string, host?: string): void => {
  if (isDisabled()) {
    return;
  }

  if (!initialized || !agent) {
    console.warn('New Relic: Not initialized. Page view name not set:', name);
    return;
  }

  agent.setPageViewName(name, host);
};

const setCurrentRouteName = (name: string): void => {
  if (isDisabled()) {
    return;
  }

  if (!initialized || !agent) {
    console.warn('New Relic: Not initialized. Route name not set:', name);
    return;
  }

  agent.setCurrentRouteName(name);
};

type LogLevel = 'ERROR' | 'TRACE' | 'DEBUG' | 'INFO' | 'WARN';

const log = (
  message: string,
  options?: { customAttributes?: Record<string, unknown>; level?: LogLevel },
): void => {
  if (isDisabled()) {
    return;
  }

  if (!initialized || !agent) {
    console.warn('New Relic: Not initialized. Log not captured:', message);
    return;
  }

  agent.log(message, options);
};

const addRelease = (name: string, id: string): void => {
  if (isDisabled()) {
    return;
  }

  if (!initialized || !agent) {
    console.warn('New Relic: Not initialized. Release not added:', name);
    return;
  }

  agent.addRelease(name, id);
};

const finished = (timeStamp?: number): void => {
  if (isDisabled()) {
    return;
  }

  if (!initialized || !agent) {
    console.warn('New Relic: Not initialized. Finished not recorded.');
    return;
  }

  agent.finished(timeStamp);
};

export const newRelicService = {
  initialize,
  setUserId,
  getUserId,
  setApplicationVersion,
  addPageAction,
  recordCustomEvent,
  noticeError,
  setCustomAttribute,
  setPageViewName,
  setCurrentRouteName,
  log,
  addRelease,
  finished,
};

export default newRelicService;
